// AllProducts.jsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts, getCategories } from "../../utils/storage";

export default function AllProducts() {
    const navigate = useNavigate();
    const cardRefs = useRef([]);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedBadge, setSelectedBadge] = useState("all");
    const [sortBy, setSortBy] = useState("newest");

    useEffect(() => {
        document.title = "All Products | Tecniqa";
        setProducts(getProducts().filter((p) => p.isActive !== false));
        setCategories(getCategories().filter((c) => c.isActive !== false));
    }, []);

    // ── Intersection Observer for card entrance animation ──
    useEffect(() => {
        const observers = cardRefs.current.map((el, i) => {
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => el.classList.add("ap-visible"), i * 60);
                        obs.disconnect();
                    }
                },
                { threshold: 0.08 }
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach((o) => o?.disconnect());
    }, [products, selectedCategory, selectedBadge, search, sortBy]);

    // ── Unique badges from all products ──
    const allBadges = useMemo(() => {
        const b = [...new Set(products.map((p) => p.badge).filter(Boolean))];
        return b;
    }, [products]);

    // ── Filtered + sorted list ──
    const filtered = useMemo(() => {
        let list = [...products];

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (p) =>
                    p.name?.toLowerCase().includes(q) ||
                    p.brand?.toLowerCase().includes(q) ||
                    p.shortDescription?.toLowerCase().includes(q)
            );
        }

        if (selectedCategory !== "all") {
            list = list.filter((p) => p.categoryId === selectedCategory);
        }

        if (selectedBadge !== "all") {
            list = list.filter((p) => p.badge === selectedBadge);
        }

        if (sortBy === "newest") {
            list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } else if (sortBy === "oldest") {
            list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        } else if (sortBy === "price-low") {
            list.sort((a, b) => Number(a.salePrice) - Number(b.salePrice));
        } else if (sortBy === "price-high") {
            list.sort((a, b) => Number(b.salePrice) - Number(a.salePrice));
        } else if (sortBy === "discount") {
            list.sort((a, b) => Number(b.discount) - Number(a.discount));
        }

        return list;
    }, [products, search, selectedCategory, selectedBadge, sortBy]);

    const handleCardClick = (product) => {
        navigate(`/category/${product.categorySlug}/${product.id}`);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

                :root {
                    --dark: #1B211A;
                    --green: #628141;
                    --green-soft: #8BAE66;
                    --cream: #EBD5AB;
                }

                /* ── PAGE ── */
                .ap-page {
                    font-family: 'DM Sans', 'Segoe UI', sans-serif;
                    min-height: 100vh;
                    background: #f7f7f5;
                    position: relative;
                    overflow: hidden;
                    padding-bottom: 80px;
                }

                /* ── HERO ── */
                .ap-hero {
                    position: relative;
                    padding: 56px 20px 100px;
                    text-align: center;
                    background: linear-gradient(145deg, #07140b 0%, #0d2414 50%, #17331f 100%);
                    overflow: hidden;
                }

                .ap-hero-grid {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(139,174,102,.07) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(139,174,102,.07) 1px, transparent 1px);
                    background-size: 56px 56px;
                    pointer-events: none;
                }

                .ap-orb {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(90px);
                    pointer-events: none;
                }

                .ap-orb1 { width: 380px; height: 380px; top: -120px; left: -80px; background: rgba(139,174,102,.16); }
                .ap-orb2 { width: 280px; height: 280px; bottom: -80px; right: -60px; background: rgba(98,129,65,.18); }

                .ap-hero-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    max-width: 900px;
                    margin: 0 auto;
                }

                .ap-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    background: rgba(139,174,102,.12);
                    border: 1px solid rgba(139,174,102,.26);
                    border-radius: 999px;
                    padding: 6px 18px;
                    margin-bottom: 22px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: .1em;
                    text-transform: uppercase;
                    color: #8BAE66;
                }

                .ap-badge-dot {
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: #8BAE66;
                    box-shadow: 0 0 6px #8BAE66;
                    animation: apPulse 1.6s ease infinite;
                }

                @keyframes apPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: .45; transform: scale(.7); }
                }

                .ap-hero-title {
                    font-family: 'Playfair Display', serif;
                    font-size: clamp(2.2rem, 5vw, 3.8rem);
                    font-weight: 700;
                    color: #fff;
                    letter-spacing: -0.02em;
                    line-height: 1.1;
                    margin-bottom: 14px;
                }

                .ap-hero-title span { color: #8BAE66; }

                .ap-hero-sub {
                    font-size: 15px;
                    color: rgba(255,255,255,0.62);
                    font-weight: 300;
                    line-height: 1.8;
                    max-width: 500px;
                    margin-bottom: 24px;
                }

                .ap-hero-meta {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                    justify-content: center;
                }

                .ap-meta-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(98,129,65,0.18);
                    border: 1px solid rgba(98,129,65,0.32);
                    color: #8BAE66;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 5px 14px;
                    border-radius: 20px;
                    letter-spacing: 0.05em;
                }

                /* ── BODY ── */
                .ap-body {
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: 0 32px;
                }

                /* ── CONTROLS CARD ── */
                .ap-controls {
                    background: #fff;
                    border-radius: 14px;
                    // border: 1px solid #ebebeb;
                    border: 1.5px solid #628141;
                    padding: 20px 24px;
                    margin-top: 32px;
                    margin-bottom: 32px;
                    position: relative;
                    z-index: 2;
                    // box-shadow: 0 0 10px #628141;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    align-items: center;
                }

                .ap-search-wrap {
                    flex: 1;
                    min-width: 200px;
                    position: relative;
                }

                .ap-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #628141;
                    font-size: 15px;
                    pointer-events: none;
                }

                .ap-search {
                    width: 100%;
                    padding: 10px 14px 10px 36px;
                    border: 1px solid #e0e0e0;
                    border-radius: 9px;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13.5px;
                    color: #111;
                    background: #fafafa;
                    outline: none;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }

                .ap-search:focus {
                    border-color: #628141;
                    box-shadow: 0 0 0 3px rgba(98,129,65,0.1);
                    background: #fff;
                }

                .ap-select {
                    padding: 10px 14px;
                    border: 1px solid #e0e0e0;
                    border-radius: 9px;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px;
                    color: #333;
                    background: #fafafa;
                    outline: none;
                    cursor: pointer;
                    min-width: 150px;
                    transition: border-color 0.2s;
                }

                .ap-select:focus {
                    border-color: #628141;
                    box-shadow: 0 0 0 3px rgba(98,129,65,0.1);
                }

                .ap-results-count {
                    font-size: 12.5px;
                    color: #888;
                    font-weight: 500;
                    white-space: nowrap;
                    margin-left: auto;
                }

                .ap-results-count strong {
                    color: #628141;
                    font-weight: 700;
                }

                /* ── GRID ── */
                .ap-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
                    gap: 22px;
                }

                /* ── CARD ── */
                .ap-card {
                    border-radius: 18px;
                    overflow: hidden;
                    background: linear-gradient(180deg, #ffffff 0%, #f9f9f9 100%);
                    border: 1px solid rgba(0,0,0,0.06);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    display: flex;
                    flex-direction: column;
                    cursor: pointer;
                    position: relative;
                    opacity: 0;
                    transform: translateY(24px);
                    transition:
                        transform 0.35s cubic-bezier(0.4,0,0.2,1),
                        box-shadow 0.35s cubic-bezier(0.4,0,0.2,1),
                        border-color 0.25s,
                        opacity 0.4s ease;
                }

                .ap-card.ap-visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                .ap-card:hover {
                    transform: translateY(-7px);
                    box-shadow: 0 0 0 1.5px rgba(98,129,65,0.3);
                    border-color: rgba(98,129,65,0.3);
                }

                /* Badge */
                .ap-card-badge {
                    position: absolute;
                    top: 13px; left: 13px;
                    z-index: 3;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #fff;
                    padding: 4px 11px;
                    border-radius: 100px;
                    background: linear-gradient(135deg, #628141, #8BAE66);
                    box-shadow: 0 3px 10px rgba(98,129,65,0.32);
                }


                /* Image */
                .ap-img-wrap {
                    height: 190px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 22px;
                    background: radial-gradient(circle at 50% 30%, rgba(98,129,65,0.1) 0%, rgba(245,245,245,0.6) 70%, transparent 100%);
                    position: relative;
                    overflow: hidden;
                }

                .ap-img-wrap::after {
                    content: '';
                    position: absolute;
                    bottom: 0; left: 0; right: 0;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(98,129,65,0.15), transparent);
                }

                .ap-img-wrap img {
                    max-height: 140px;
                    max-width: 100%;
                    object-fit: contain;
                    filter: drop-shadow(0 6px 16px rgba(0,0,0,0.09));
                    transition: transform 0.42s cubic-bezier(0.4,0,0.2,1);
                }

                .ap-card:hover .ap-img-wrap img {
                    transform: scale(1.09) rotate(-1deg);
                    filter: drop-shadow(0 12px 24px rgba(98,129,65,0.16));
                }

                /* No image fallback */
                .ap-img-placeholder {
                    width: 70px; height: 70px;
                    border-radius: 14px;
                    background: rgba(98,129,65,0.08);
                    border: 1px dashed rgba(98,129,65,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    color: rgba(98,129,65,0.4);
                }

                /* Body */
                .ap-card-body {
                    padding: 16px 16px 0;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .ap-card-brand {
                    font-size: 10.5px;
                    font-weight: 700;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: #628141;
                }

                .ap-card-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #111;
                    line-height: 1.45;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }


                /* Availability */
                .ap-availability {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11.5px;
                    font-weight: 600;
                    color: #628141;
                    padding-bottom: 4px;
                }

                .ap-avail-dot {
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: #628141;
                    box-shadow: 0 0 5px rgba(98,129,65,0.5);
                }

                .ap-avail-dot.out { background: #e06060; box-shadow: 0 0 5px rgba(224,96,96,0.4); }
                .ap-availability.out { color: #c04040; }

                /* CTA */
                .ap-card-footer {
                    padding: 12px 16px 16px;
                }

                .ap-cta {
                    width: 100%;
                    padding: 11px;
                    border: none;
                    border-radius: 11px;
                    background: #1B211A;
                    color: #fff;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    min-height: 44px;
                    letter-spacing: 0.02em;
                    opacity: 0;
                    transform: translateY(8px);
                    transition: opacity 0.26s ease, transform 0.26s ease, background 0.2s;
                }

                .ap-card:hover .ap-cta {
                    opacity: 1;
                    transform: translateY(0);
                }

                .ap-cta:hover { background: #628141; }

                /* ── EMPTY STATE ── */
                .ap-empty {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 72px 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 14px;
                }

                .ap-empty-icon {
                    width: 72px; height: 72px;
                    border-radius: 18px;
                    background: rgba(98,129,65,0.08);
                    border: 1px dashed rgba(98,129,65,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    color: rgba(98,129,65,0.45);
                }

                .ap-empty h3 {
                    font-family: 'Playfair Display', serif;
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #1B211A;
                }

                .ap-empty p {
                    font-size: 14px;
                    color: #888;
                    max-width: 320px;
                    line-height: 1.7;
                }

                .ap-empty-reset {
                    padding: 10px 22px;
                    border: 1.5px solid rgba(98,129,65,0.4);
                    border-radius: 99px;
                    background: transparent;
                    color: #628141;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .ap-empty-reset:hover {
                    background: rgba(98,129,65,0.08);
                    border-color: #628141;
                }

                /* ── RESPONSIVE ── */
                @media (max-width: 900px) {
                    .ap-body { padding: 0 20px; }
                    .ap-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
                }

                @media (max-width: 640px) {
                    .ap-hero { padding: 40px 16px 80px; }
                    .ap-body { padding: 0 14px; }
                    .ap-controls { flex-direction: column; align-items: stretch; }
                    .ap-results-count { margin-left: 0; }
                    .ap-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
                    .ap-cta { opacity: 1; transform: translateY(0); }
                }

                @media (max-width: 420px) {
                    .ap-grid { grid-template-columns: 1fr; }
                }
            `}</style>

            <div className="ap-page">

                {/* HERO */}
                <div className="ap-hero">
                    <div className="ap-hero-grid" />
                    <div className="ap-orb ap-orb1" />
                    <div className="ap-orb ap-orb2" />

                    <div className="ap-hero-content">
                        <div className="ap-hero-badge">
                            <div className="ap-badge-dot" />
                            Catalogue
                        </div>

                        <h1 className="ap-hero-title">
                            All <span>Products</span>
                        </h1>

                        <p className="ap-hero-sub">
                            Browse our complete range of industrial-grade HVACR tools,
                            refrigeration components, and professional equipment.
                        </p>

                        <div className="ap-hero-meta">
                            <span className="ap-meta-badge">{products.length} Products</span>
                            <span className="ap-meta-badge">{categories.length} Categories</span>
                        </div>
                    </div>
                </div>

                {/* BODY */}
                <div className="ap-body">

                    {/* CONTROLS */}
                    <div className="ap-controls">

                        {/* Search */}
                        <div className="ap-search-wrap">
                            <span className="ap-search-icon">⌕</span>
                            <input
                                className="ap-search"
                                type="text"
                                placeholder="Search products, brands…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {/* Category filter */}
                        <select
                            className="ap-select"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>

                        {/* Badge filter */}
                        <select
                            className="ap-select"
                            value={selectedBadge}
                            onChange={(e) => setSelectedBadge(e.target.value)}
                        >
                            <option value="all">All Badges</option>
                            {allBadges.map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>

                        {/* Sort */}
                        <select
                            className="ap-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="discount">Best Discount</option>
                        </select>

                        <span className="ap-results-count">
                            Showing <strong>{filtered.length}</strong> of {products.length}
                        </span>
                    </div>

                    {/* GRID */}
                    <div className="ap-grid">
                        {filtered.length === 0 ? (
                            <div className="ap-empty">
                                <div className="ap-empty-icon">⊘</div>
                                <h3>No Products Found</h3>
                                <p>
                                    {products.length === 0
                                        ? "No products have been added yet. Add products from the admin panel."
                                        : "No products match your current filters. Try adjusting your search or filters."}
                                </p>
                                {products.length > 0 && (
                                    <button
                                        className="ap-empty-reset"
                                        onClick={() => {
                                            setSearch("");
                                            setSelectedCategory("all");
                                            setSelectedBadge("all");
                                            setSortBy("newest");
                                        }}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            filtered.map((product, i) => {
                                const isOutOfStock = /out of stock/i.test(product.availability);
                                return (
                                    <div
                                        key={product.id}
                                        className="ap-card"
                                        ref={(el) => (cardRefs.current[i] = el)}
                                        onClick={() => handleCardClick(product)}
                                    >
                                        {/* Badge */}
                                        {product.badge && (
                                            <div className="ap-card-badge">{product.badge}</div>
                                        )}

                                        

                                        {/* Image */}
                                        <div className="ap-img-wrap">
                                            {product.images?.[0] ? (
                                                <img
                                                    src={product.images[0]}
                                                    alt={product.name}
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="ap-img-placeholder">⬡</div>
                                            )}
                                        </div>

                                        {/* Body */}
                                        <div className="ap-card-body">
                                            {product.brand && (
                                                <div className="ap-card-brand">{product.brand}</div>
                                            )}
                                            <div className="ap-card-name">{product.name}</div>
                                            

                                            

                                            {/* Availability */}
                                            <div className={`ap-availability${isOutOfStock ? " out" : ""}`}>
                                                <div className={`ap-avail-dot${isOutOfStock ? " out" : ""}`} />
                                                {product.availability || "In Stock"}
                                            </div>
                                        </div>

                                        {/* CTA */}
                                        <div className="ap-card-footer">
                                            <button
                                                className="ap-cta"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCardClick(product);
                                                }}
                                            >
                                                View Product
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}